import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commercialApi, validationApi } from '@/api/commercial';
import { toast } from '@/stores/toastStore';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Hooks TanStack Query.
 *
 * Toutes les mutations invalident les caches croisés utiles :
 *   - creerProforma        → dashboard + proformas
 *   - soumettreProforma    → dashboard + proformas + validation count
 *   - validerCommande      → dashboard + proformas + validation count + commandes
 *   - renvoyerCommande     → dashboard + proformas + validation count
 */

// ─── Query keys ────────────────────────────────────────────────

const K_DASHBOARD    = ['commercial', 'dashboard'] as const;
const K_PROFORMAS    = ['commercial', 'proformas'] as const;
const K_COMMANDES    = ['commercial', 'commandes'] as const;
const K_PROSPECTS    = ['commercial', 'prospects'] as const;
const K_VALIDATIONS  = ['validation', 'en-attente'] as const;
const K_VAL_COUNT    = ['validation', 'count'] as const;

// ─── Commercial : lectures ────────────────────────────────────

export const useCommercialDashboard = () =>
  useQuery({
    queryKey:  K_DASHBOARD,
    queryFn:   commercialApi.dashboard,
    staleTime: 30_000,
  });

export const useMesProformas = (statut?: string) =>
  useQuery({
    queryKey:  [...K_PROFORMAS, statut ?? 'all'] as const,
    queryFn:   () => commercialApi.mesProformas(statut),
    staleTime: 30_000,
  });

export const useMesCommandes = () =>
  useQuery({
    queryKey:  K_COMMANDES,
    queryFn:   commercialApi.mesCommandes,
    staleTime: 30_000,
  });

export const useMesProspects = () =>
  useQuery({
    queryKey:  K_PROSPECTS,
    queryFn:   commercialApi.mesProspects,
    staleTime: 60_000,
  });

// ─── Commercial : mutations ───────────────────────────────────

export const useCreerProforma = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: commercialApi.creerProforma,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      toast.success('Proforma créée', data.reference);
    },
  });
};

export const useModifierProforma = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof commercialApi.modifierProforma>[1] }) =>
      commercialApi.modifierProforma(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      toast.success('Proforma modifiée');
    },
  });
};

export const useAttacherSignature = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) =>
      commercialApi.attacherSignature(id, url),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      toast.success('Signature attachée');
    },
  });
};

export const useSoumettreProforma = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: commercialApi.soumettreProforma,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      toast.success('Soumise au DAF', data.reference);
    },
  });
};

// ─── Validation DAF ───────────────────────────────────────────

export const useValidationEnAttente = () =>
  useQuery({
    queryKey:  K_VALIDATIONS,
    queryFn:   validationApi.enAttente,
    staleTime: 15_000,
  });

/** TICKET-004 C-04 : toutes les files en attente du DAF. */
export const useATraiter = () =>
  useQuery({ queryKey: ['validation', 'a-traiter'] as const, queryFn: validationApi.aTraiter, staleTime: 15_000 });

export const useValidationCount = () =>
  useQuery({
    queryKey:        K_VAL_COUNT,
    queryFn:         validationApi.count,
    staleTime:       15_000,
    refetchInterval: 60_000, // rafraîchit le badge sans reload
  });

export const useKpiVentesMois = () =>
  useQuery({
    queryKey:  ['validation', 'kpi', 'ventes-mois'] as const,
    queryFn:   validationApi.kpiVentesMois,
    staleTime: 5 * 60_000,
  });

export const useKpiAchatsMois = () =>
  useQuery({
    queryKey:  ['validation', 'kpi', 'achats-mois'] as const,
    queryFn:   validationApi.kpiAchatsMois,
    staleTime: 5 * 60_000,
  });

export const useKpiProductionMois = () =>
  useQuery({
    queryKey:  ['validation', 'kpi', 'production-mois'] as const,
    queryFn:   validationApi.kpiProductionMois,
    staleTime: 5 * 60_000,
  });

export const useValiderCommande = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: validationApi.validerCommande,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_VALIDATIONS });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      qc.invalidateQueries({ queryKey: K_COMMANDES });
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      toast.success('Commande validée', data.reference);
    },
  });
};

export const useRenvoyerCommande = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      validationApi.renvoyerCommande(id, motif),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_VALIDATIONS });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      qc.invalidateQueries({ queryKey: K_PROFORMAS });
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      toast.success('Renvoyée pour correction', data.reference);
    },
  });
};
